import Vue from 'vue'
import {make} from 'vuex-pathify'
import {ptracGetEntities, ptracPatchEntity} from '@/utilities/ptracApi'
import Constants from '@/utilities/constants'
import {OK, NOT_FOUND, BAD_REQUEST} from 'http-status-codes'

const initialState = {
    purchaseOrder: null,
    originalPurchaseOrder: null,
    mostRecentAcceptedVersionOfChangePO: null,
    firstNotAcceptedVersionOfChangePO: null,
    ptracProject: null,
    ptracJobs: [],
    allRequestedChangesMade: null,
    changesFound: [] // Array to store found changes as objects in format {key, originalValue, changedValue}
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)
const mutations = make.mutations(state)

mutations.reset = () => {

    // Use Vue.set to make getters/sync reactive.  Otherwise they will return stale data
    for (const key of Object.keys(state)) {
        Vue.set(state, key, initialState[key])
    }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions: {
        async fetchPurchaseOrder({state}, purchaseOrderId) {
            let response = await ptracGetEntities('supply-pro-purchase-orders/findByPurchaseOrderId?purchaseOrderId=' + purchaseOrderId)
            if (OK === response.status) {
                state.purchaseOrder = response.data
            }
            // handle error
        },
        async fetchDataForChangeOrCancel({dispatch, state}, purchaseOrderId) {
            let response = await ptracGetEntities('supply-pro-purchase-orders/findByPurchaseOrderId?purchaseOrderId=' + purchaseOrderId)
            if (OK !== response.status) {
                // console.log("Could not find PO with id#" + purchaseOrderId)
                return response
            }

            state.purchaseOrder = response.data
            //console.log("ChangePurchaseOrder found and data loaded")

            // Search by builderOrderNumber to find Original Purchase Order
            response = await dispatch('findOriginalPurchaseOrder', state.purchaseOrder.header.builderOrderNumber)

            if (OK !== response.status || state.originalPurchaseOrder === null) {
                // console.log("Could not find an original PO with this builder PO#: " + state.purchaseOrder.header.builderOrderNumber)
                response.status = NOT_FOUND
                return response
            }

            // console.log("Original purchase order found: " + state.originalPurchaseOrder.id)

            // Search by purchaseOrderId to find most recent accepted version of change PO (if any)
            response = await dispatch('findMostRecentAcceptedVersionOfChangePO', state.purchaseOrder.id)

            if (OK !== response.status) {
                response.status = BAD_REQUEST
                return response
            }
            if (state.mostRecentAcceptedVersionOfChangePO === null) {
                // console.log("Could not find a previous version of this PO that has been accepted into PTRAC")
                return response
            }

            // Search by purchaseOrderId to check if there are previous POs in changeOrderSequence that need to be dealt with first
            response = await dispatch('findFirstNotAcceptedVersionOfChangePO', state.purchaseOrder.id)

            if (OK !== response.status) {
                response.status = BAD_REQUEST
                return response
            }
            if (state.firstNotAcceptedVersionOfChangePO) {
                // console.log("There is a previous version of this ChangePO that needs to be dealt with first")
                return response
            }


            // Check that original POs jobNum has already been mapped to PTRAC project (for a change or cancel it should be unless Original hasnt been processed yet)
            if (state.originalPurchaseOrder.matchingProjectId === null) {
                // console.log("Original PO (id#: " + state.originalPurchaseOrder.id +") has not yet been matched to a PTRAC project")
                return response  // return with status = OK, missing data will be dealt with in ModifyPurchaseOreder
            }

            // get the associated project (check matchingProjectId of original PO, in case its not on new one?)
            response = await ptracGetEntities('projects/' + state.originalPurchaseOrder.matchingProjectId)
            if (OK !== response.status) {
                // console.log("Original PO had a matchingProjectId, but we cant find that project!")
                return response
            }

            state.ptracProject = response.data
            // console.log("associated PTRAC Project found: " + state.ptracProject.id)

            // get all the ptrac job Ids that are associated with the original PO#
            response = await ptracGetEntities('supply-pro-purchase-orders/findJobsByPurchaseOrderId?purchaseOrderId=' + state.originalPurchaseOrder.id)
            if (OK !== response.status) {
                // console.log("Can't find any jobIds associated with original PO#: " + state.originalPurchaseOrder.id)
                return response
            }
            state.ptracJobs = response.data
            // console.log("Associated PTRAC jobs found: " + state.ptracJobs)
            return response
        },

        async findOriginalPurchaseOrder({state}, builderOrderNumber) {
            // This method assumes there is only one Original PO with the same builderOrderNumber - breaks otherwise (will just return last one)
            const response = await ptracGetEntities('supply-pro-purchase-orders/findByOrderNumber?orderNumber=' + builderOrderNumber)
            if (OK === response.status) {
                for (const purchaseOrder of response.data) {
                    if (purchaseOrder.purchaseOrderType === Constants.SUPPLYPRO.PO_TYPES.ORIGINAL) {
                        state.originalPurchaseOrder = purchaseOrder
                    }
                }
            }
            return response
        },

        async findMostRecentAcceptedVersionOfChangePO({state}, purchaseOrderId) {
            const response = await ptracGetEntities('supply-pro-purchase-orders/findMostRecentAcceptedVersionOfChangePurchaseOrder?purchaseOrderId=' + purchaseOrderId)
            if (OK === response.status) {
                state.mostRecentAcceptedVersionOfChangePO = response.data
            } else {
                // need some sort of actual error handling since null indicates no previous versions are enteredInPtrac - what if an Error comes back (some prev PO not found)
                state.mostRecentAcceptedVersionOfChangePO ="error"
            }
            return response
        },

        async findFirstNotAcceptedVersionOfChangePO({state}, purchaseOrderId) {
            const response = await ptracGetEntities('supply-pro-purchase-orders/findFirstNotAcceptedVersionOfChangePurchaseOrder?purchaseOrderId=' + purchaseOrderId)
            if (OK === response.status) {
                state.firstNotAcceptedVersionOfChangePO = response.data
            } else {
                // need some sort of actual error handling since null indicates all previous versions are enteredInPtrac - what if an Error comes back (some prev PO not found)
                state.firstNotAcceptedVersionOfChangePO = "error"
            }
            return response
        },

        async markPurchaseOrderAsAccepted({state, commit}) {
            const response = await ptracPatchEntity('supply-pro-purchase-orders/markPurchaseOrderAsAccepted?purchaseOrderId=' + state.purchaseOrder.id)

            if (OK === response.status) {
                // remove the purchase order from the purchaseOrders list
                commit('supplyPro/search/delete', state.purchaseOrder.id, { root: true })
            }

            return response
        },

        async findChanges({state}) {
            // changesFound not resetting properly when reset() mutation is called - do it manually here
            state.changesFound = []
            if (!(state.purchaseOrder && state.mostRecentAcceptedVersionOfChangePO)) {
                return {'status': BAD_REQUEST, 'data':'Cant compare without having both a purchaseOrder and a mostRecentAcceptedVersionOfChangePO'}
            }
            const changeHeader = state.purchaseOrder.header
            const originalHeader = state.mostRecentAcceptedVersionOfChangePO.header
            const headerKeys = ['startDate', 'endDate' ]
            compare('header.', headerKeys, originalHeader, changeHeader)

            const changeTask = changeHeader.task
            const originalTask = originalHeader.task
            const taskKeys = ['taskNum', 'name', 'description']
            compare('header.task.', taskKeys, originalTask, changeTask)

            const changeJob = changeHeader.job
            const originalJob = originalHeader.job
            const jobKeys = ['jobNum', 'name', 'street', 'streetSupplement', 'city', 'stateCode', 'postalCode', 'subdivision', 'phase', 'lot', 'block', 'plan', 'elevation', 'permitNumber', 'startDate', 'endDate', 'primaryFirstName', 'primaryLastName', 'primaryPhoneNumber', 'primaryEmailAddress', 'communityId']
            compare('header.job.', jobKeys, originalJob, changeJob)

            const changeItems = state.purchaseOrder.items
            const originalItems = state.mostRecentAcceptedVersionOfChangePO.items
            const itemKeys = ['itemDescription', 'quantity', 'requestedUnitPrice', 'total']

            // If the most recent accepted version is a change PO
            if (state.mostRecentAcceptedVersionOfChangePO.purchaseOrderType !== Constants.SUPPLYPRO.PO_TYPES.ORIGINAL) {
                for (const changeItem of changeItems) {
                    for (const originalItem of originalItems) {
                       if (changeItem.originalLineNum === originalItem.originalLineNum) {
                        compare(`Line Item #${changeItem.originalLineNum}: `, itemKeys, originalItem.originalItemDetailWithChanges, changeItem.originalItemDetailWithChanges)
                       }
                    }
                }
            }
            // If the most recent accepetd version of PO is the Original
            // needs its own compare function because of different shape of the items data in changePO
            for (const changeItem of changeItems) {
                for (const originalItem of originalItems) {
                    if (changeItem.originalLineNum === originalItem.builderLineItemNum) {
                        for (const key of itemKeys) {
                            if (changeItem.originalItemDetailWithChanges[key] !== originalItem[key]) {
                                state.changesFound.push({'key': 'Line Item #' + changeItem.originalLineNum + ' ' + key,
                                                'originalValue': originalItem[key],
                                                'changedValue': changeItem.originalItemDetailWithChanges[key]
                                })
                            }
                        }
                    }
                }
            }

            return {'status': OK, 'data':'Changes found and stored in purchaseOrder.state.changesFound'}

            function compare(objectPath, keySet, original, change) {
                for (const key of keySet) {
                    if (change[key] !== original[key]) {
                        state.changesFound.push({'key': objectPath + key,
                                        'originalValue': original[key],
                                        'changedValue': change[key]
                        })
                    }
                }
            }

            // Need to deal with header.options? (not currently extracting that into PurchaseOrder object)

            // Unclear what a PO that ADDS or REMOVES line items entirely would look like in this format. Would that ever happen?

        }

    }
}
